import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertTriangle, X, Info } from 'lucide-react';

/**
 * Minimal, editorial-styled transient toast system — the missing piece that lets
 * mutations surface success/error feedback consistently (REVIEW follow-up F-J1-1).
 *
 * Usage:
 *   const toast = useToast();
 *   const m = useMutation({ mutationFn, onError: (e) => toast.error(errMessage(e)) });
 *
 * Intentionally dependency-free (no Radix) so it inherits the design tokens
 * (hairlines, slate + emerald/rose) and honors prefers-reduced-motion via CSS.
 */

type ToastKind = 'success' | 'error' | 'info';
interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Hook to push toasts. Safe to call even if the provider is absent (no-op). */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  return (
    ctx ?? {
      success: () => undefined,
      error: () => undefined,
      info: () => undefined,
    }
  );
}

/** Normalize an unknown thrown value (axios error / Error / string) to a message. */
export function errMessage(e: unknown, fallback = 'Something went wrong'): string {
  const anyE = e as { response?: { data?: { message?: unknown } }; message?: unknown };
  const apiMsg = anyE?.response?.data?.message;
  if (typeof apiMsg === 'string' && apiMsg.trim()) return apiMsg;
  if (Array.isArray(apiMsg) && typeof apiMsg[0] === 'string') return apiMsg[0];
  if (typeof anyE?.message === 'string' && anyE.message.trim()) return anyE.message;
  return fallback;
}

const ICONS: Record<ToastKind, typeof Info> = {
  success: CheckCircle2,
  error: AlertTriangle,
  info: Info,
};
const TONE: Record<ToastKind, string> = {
  success: 'text-emerald-600',
  error: 'text-rose-600',
  info: 'text-muted-foreground',
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
    const tm = timers.current[id];
    if (tm) {
      clearTimeout(tm);
      delete timers.current[id];
    }
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string) => {
      const id = ++seq.current;
      setItems((prev) => [...prev.slice(-3), { id, kind, message }]);
      timers.current[id] = setTimeout(() => dismiss(id), kind === 'error' ? 7000 : 4000);
    },
    [dismiss],
  );

  const api = useRef<ToastApi>({
    success: (m) => push('success', m),
    error: (m) => push('error', m),
    info: (m) => push('info', m),
  });
  // Keep the ref's closures pointing at the latest push.
  api.current.success = (m) => push('success', m);
  api.current.error = (m) => push('error', m);
  api.current.info = (m) => push('info', m);

  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  return (
    <ToastContext.Provider value={api.current}>
      {children}
      {createPortal(
        <div
          className="fixed bottom-4 right-4 z-[80] flex flex-col gap-2 w-[min(92vw,360px)]"
          role="region"
          aria-label="Notifications"
        >
          {items.map((t) => {
            const Icon = ICONS[t.kind];
            return (
              <div
                key={t.id}
                role={t.kind === 'error' ? 'alert' : 'status'}
                className="flex items-start gap-2.5 rounded-md border border-border bg-card px-3.5 py-2.5 shadow-lg animate-[toastin_140ms_ease-out]"
              >
                <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${TONE[t.kind]}`} />
                <p className="flex-1 text-[13px] leading-snug text-foreground">{t.message}</p>
                <button
                  onClick={() => dismiss(t.id)}
                  aria-label="Dismiss"
                  className="text-muted-foreground hover:text-foreground shrink-0"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}
